import React, { useState, useRef } from 'react';
import {
  Dialog, Button, Box, Alert, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, CircularProgress,
  Checkbox, Stack, Chip, Divider,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { DialogHeader } from '../common/AppDialog';
import { parseImportedBudgetsCSV, getBudgetCSVTemplate } from '../../utils/budgetsExportUtils';

const BudgetImportDialog = ({ open, onClose, categories, accounts, onImport, isLoading = false }) => {
  const [file, setFile] = useState(null);
  const [selectedBudgets, setSelectedBudgets] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [parsedBudgets, setParsedBudgets] = useState([]);
  const [step, setStep] = useState('upload');
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    setParseError(null);
    setWarnings([]);
    setSelectedBudgets([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const { budgets, warnings: parseWarnings } = parseImportedBudgetsCSV(e.target.result, categories, accounts);
        setParsedBudgets(budgets);
        setWarnings(parseWarnings);
        if (budgets.length > 0) {
          setSelectedBudgets(budgets.map((_, idx) => idx));
          setStep('review');
        } else {
          setParseError(parseWarnings.length ? parseWarnings[0] : 'No valid budgets found in CSV');
        }
      } catch (error) {
        setParseError(error.message || 'Failed to parse CSV file');
        setParsedBudgets([]);
        setWarnings([]);
      }
    };
    reader.onerror = () => setParseError('Failed to read file');
    reader.readAsText(selectedFile);
  };

  const handleSelectAll = (checked) => {
    setSelectedBudgets(checked ? parsedBudgets.map((_, idx) => idx) : []);
  };

  const handleSelectBudget = (index) => {
    setSelectedBudgets(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const handleImport = async () => {
    const toImport = selectedBudgets.map(idx => parsedBudgets[idx]);
    setStep('importing');
    try {
      await onImport(toImport);
      handleClose();
    } catch (error) {
      setParseError(error.message || 'Failed to import budgets');
      setStep('review');
    }
  };

  const handleDownloadTemplate = () => {
    const template = getBudgetCSVTemplate();
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'budgets_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    setFile(null);
    setSelectedBudgets([]);
    setParseError(null);
    setWarnings([]);
    setParsedBudgets([]);
    setStep('upload');
    onClose();
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.name : catId || '—';
  };

  const getAccountName = (accId) => {
    const acc = accounts.find(a => a.id === accId);
    return acc ? acc.name : accId || '—';
  };

  const renderTemplateInfo = () => (
    <Stack spacing={2}>
      <Box sx={{ p: 1.5, backgroundColor: '#fff', borderRadius: 1.5, border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" fontWeight="600" color="primary" sx={{ mb: 0.75 }}>
          Required Columns
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, display: 'block' }}>
          <b>Name</b>: Budget name<br />
          <b>Category</b>: Category name (matched to existing categories)<br />
          <b>Account</b>: Account name (matched to existing accounts)<br />
          <b>Amount</b>: Budget amount (number)<br />
          <b>Type</b>: Expense or Income<br />
          <b>Start Date</b>: YYYY-MM-DD format<br />
          <b>Recurring</b>: Yes or No<br />
          <b>Period</b>: weekly, fortnightly, monthly, quarterly, yearly<br />
          <b>Prorated</b>: Yes or No<br />
          <b>Active</b>: Yes or No
        </Typography>
      </Box>
      <Box sx={{ p: 1.5, backgroundColor: '#fff', borderRadius: 1.5, border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" fontWeight="600" color="primary" sx={{ mb: 0.75 }}>
          Optional Columns
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, display: 'block' }}>
          <b>End Date</b>: YYYY-MM-DD format<br />
          <b>Notes</b>: Any notes
        </Typography>
      </Box>
      <Box sx={{ p: 1.5, backgroundColor: '#fff', borderRadius: 1.5, border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" fontWeight="600" color="primary" sx={{ mb: 0.75 }}>
          Example
        </Typography>
        <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#555', lineHeight: 1.6, overflowX: 'auto', whiteSpace: 'pre' }}>
{`Name,Category,Account,Amount,Type,Start Date,Recurring,Period,Prorated,Active
Groceries,Groceries,Main Account,500,Expense,2025-01-01,Yes,monthly,No,Yes
Salary,Income,Main Account,5000,Income,2025-01-01,Yes,monthly,No,Yes`}
        </Box>
      </Box>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      <DialogHeader
        title="Import Budgets"
        subtitle="Upload a CSV file to import budget entries"
        onClose={handleClose}
      />

      {/* Content */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden', flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Left Panel */}
        <Box sx={{ flex: { xs: 1, md: '0 0 60%' }, overflow: 'auto', p: { xs: 2, sm: 3 }, borderRight: { md: '2px solid #e0e0e0' }, backgroundColor: '#fff' }}>
          {step === 'upload' && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CloudUploadIcon sx={{ color: '#F41468' }} />
                  Upload CSV File
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  Select a CSV file with your budgets. Budgets will be added to your existing list.
                </Typography>
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    border: '2px dashed',
                    borderColor: file ? '#4caf50' : '#ccc',
                    borderRadius: 2,
                    padding: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: file ? '#f0fdf4' : '#fafafa',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#F41468', backgroundColor: '#fce4ec' }
                  }}
                >
                  {file ? (
                    <>
                      <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{file.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Click to change file</Typography>
                    </>
                  ) : (
                    <>
                      <CloudUploadIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>Click to select a CSV file</Typography>
                      <Typography variant="caption" sx={{ color: '#999' }}>.csv files only</Typography>
                    </>
                  )}
                </Box>
                <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} />
              </Box>

              {parseError && <Alert severity="error" icon={<ErrorIcon />}>{parseError}</Alert>}

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon sx={{ color: '#F41468' }} />
                  CSV Template
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  Not sure how to format your file? Download the template below.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadTemplate}
                  sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 500 }}
                >
                  Download Template
                </Button>
              </Box>
            </Stack>
          )}

          {step === 'review' && (
            <Stack spacing={2}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#F41468' }} />
                Review Budgets
              </Typography>

              {warnings.length > 0 && (
                <Alert severity="warning" icon={<WarningIcon />}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                    {warnings.length} row(s) skipped:
                  </Typography>
                  <Box component="ul" sx={{ mb: 0, pl: 2 }}>
                    {warnings.map((w, idx) => (
                      <Typography key={idx} variant="caption" component="li" sx={{ display: 'block', mb: 0.25 }}>{w}</Typography>
                    ))}
                  </Box>
                </Alert>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight={500}>
                  {selectedBudgets.length} of {parsedBudgets.length} budgets selected
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 400, overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#fce4ec', position: 'sticky', top: 0, zIndex: 1 }}>
                      <TableCell padding="checkbox" width="50px">
                        <Checkbox
                          checked={selectedBudgets.length === parsedBudgets.length && parsedBudgets.length > 0}
                          indeterminate={selectedBudgets.length > 0 && selectedBudgets.length < parsedBudgets.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          sx={{ color: '#F41468', '&.Mui-checked': { color: '#F41468' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Account</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Recurring</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedBudgets.map((budget, idx) => (
                      <TableRow
                        key={idx}
                        hover
                        onClick={() => handleSelectBudget(idx)}
                        sx={{ cursor: 'pointer', backgroundColor: selectedBudgets.includes(idx) ? '#fce4ec33' : 'transparent' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedBudgets.includes(idx)}
                            onChange={() => handleSelectBudget(idx)}
                            sx={{ color: '#F41468', '&.Mui-checked': { color: '#F41468' } }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{budget.name || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          <Chip label={getCategoryName(budget.categoryId)} size="small" variant="outlined" sx={{ borderColor: '#F41468', color: '#F41468' }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          <Chip label={getAccountName(budget.accountId)} size="small" variant="outlined" sx={{ borderColor: '#1976d2', color: '#1976d2' }} />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.875rem', fontWeight: 600, color: budget.amount > 0 ? 'success.main' : 'error.main' }}>
                          {Number(budget.amount).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>{budget.type}</TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          {budget.isRecurring ? <Chip label={budget.recurringPeriod} size="small" sx={{ bgcolor: '#fce4ec', color: '#F41468', fontWeight: 500 }} /> : 'No'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}

          {step === 'importing' && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2, color: '#F41468' }} />
              <Typography variant="body1" fontWeight={500}>
                Importing {selectedBudgets.length} budget(s)...
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right Panel */}
        <Box sx={{ flex: { xs: 0, md: '0 0 40%' }, overflow: 'auto', p: { xs: 2, sm: 3 }, backgroundColor: '#fafafa', display: { xs: 'none', md: 'block' } }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: '#F41468' }} />
            CSV Format Guide
          </Typography>
          {renderTemplateInfo()}
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 3,
        py: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}>
        <Typography variant="body2" color="text.secondary">
          {step === 'upload' && 'Upload a CSV file to get started'}
          {step === 'review' && `${parsedBudgets.length} budget(s) found in file`}
          {step === 'importing' && 'Please wait...'}
        </Typography>
        <Stack direction="row" spacing={1.5}>
          {step === 'review' && (
            <Button onClick={() => { setStep('upload'); setFile(null); setParsedBudgets([]); setSelectedBudgets([]); }} color="inherit" sx={{ color: 'text.secondary' }}>
              Back
            </Button>
          )}
          <Button onClick={handleClose} color="inherit" sx={{ color: 'text.secondary' }}>Cancel</Button>
          {step === 'review' && (
            <Button
              onClick={handleImport}
              variant="contained"
              disabled={selectedBudgets.length === 0 || isLoading}
              sx={{ px: 3 }}
            >
              Import {selectedBudgets.length} Budget{selectedBudgets.length !== 1 ? 's' : ''}
            </Button>
          )}
        </Stack>
      </Box>
    </Dialog>
  );
};

export default BudgetImportDialog;
